import {
  APP_BASE_HREF,
  CommonModule,
  HashLocationStrategy,
  LocationStrategy,
  PathLocationStrategy,
} from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, ErrorHandler, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { MATERIAL_SANITY_CHECKS } from '@angular/material/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { SentryErrorHandler } from '@app/core/error-handlers/sentry-error.handler';
import { appEffects } from '@app/store/effects';
import * as fromRoot from '@app/store/reducers';
import { provideTransloco, TranslocoModule } from '@jsverse/transloco';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { AppComponent } from './app/app.component';
import { AppRoutingModule } from './app/app-routing.module';
import { AVAILABLE_COUNTRIES, AvailableCountry } from './app/core/const/country';
import { AvailableLang, getDefaultLang } from './app/core/const/i18n';
import { CoreModule } from './app/core/core.module';
import { entitiesEffects } from './app/core/entities/effects';
import { HttpLoader } from './app/http-loader';
import { SharedModule } from './app/shared/shared.module';
import { AuthActions } from './app/store/actions';
import { environment } from './environments/environment';

function getBaseLocation(): string {
  const url = new URL(location.href);
  const queryHasAuthParams = url.searchParams;
  const relayState = queryHasAuthParams.get('RelayState');
  if (relayState && AVAILABLE_COUNTRIES.find(availableCountry => availableCountry === relayState)) {
    return '/' + relayState;
  }

  const paths: string[] = location.hash.split('/').splice(1, 1);
  const basePath: string =
    paths?.[0] && AVAILABLE_COUNTRIES.find(availableCountry => availableCountry === paths[0])
      ? paths[0]
      : AvailableCountry.fra;
  return '/' + basePath;
}

function authInitializer(store: Store<fromRoot.State>) {
  return (): void => {
    const url = new URL(location.href);
    if (url.hash.match(new RegExp('.*\\/logout\\/?$'))) {
      store.dispatch(AuthActions.logoutUserSuccess());
      return;
    }

    const queryHasAuthParams = url.searchParams;
    const samlResponse = queryHasAuthParams.get('SAMLResponse');

    if (samlResponse) {
      store.dispatch(AuthActions.authenticateUser({ samlResponse }));
    }
  };
}

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      CommonModule,
      AppRoutingModule,
      CoreModule,
      SharedModule,
      TranslocoModule,
      StoreModule.forRoot(fromRoot.rootReducers, {
        runtimeChecks: {
          strictActionSerializability: false,
          strictStateSerializability: false,
          strictActionTypeUniqueness: false,
          strictActionImmutability: false,
          strictStateImmutability: false,
          strictActionWithinNgZone: false,
        },
      }),
      EffectsModule.forRoot([...entitiesEffects, ...appEffects]),
      StoreDevtoolsModule.instrument({
        name: 'Store DevTools',
        serialize: true,
        logOnly: environment.reduxLogOnly,
      })
    ),
    { provide: LocationStrategy, useClass: environment.useHash ? HashLocationStrategy : PathLocationStrategy },
    { provide: APP_BASE_HREF, useValue: getBaseLocation() },
    provideAppInitializer(() => {
      const initializerFn = authInitializer(inject(Store));
      initializerFn();
    }),
    { provide: ErrorHandler, useClass: SentryErrorHandler },
    provideTransloco({
      config: {
        availableLangs: [AvailableLang.en, AvailableLang.fr, AvailableLang.es, AvailableLang.nl],
        defaultLang: getDefaultLang(),
        reRenderOnLangChange: true,
        fallbackLang: AvailableLang.en,
      },
      loader: HttpLoader,
    }),
    {
      provide: MATERIAL_SANITY_CHECKS,
      useValue: {
        doctype: true,
        // disable theme sanity checks because it returns a false negative, we know that the theme has been loaded so there is no need for this check.
        theme: false,
        version: true,
        hammer: true,
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch(err => {
  console.error(err);
});
